import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Mail, Calendar, MoreHorizontal, Eye, Edit, FileText, Copy, Archive, Linkedin, Phone, Megaphone, Clock, Timer } from 'lucide-react'
import { Campaign } from '@/hooks/useCampaigns'
import { Client } from '@/hooks/useClients'
import { useCachedMultipleCampaignsPerformance } from '@/hooks/useCachedCampaignPerformance'
import { useMultipleCampaignsOperationalData } from '@/hooks/useMultipleCampaignsOperationalData'
import { useOptimizedCampaignPerformance } from '@/hooks/useOptimizedCampaignPerformance'
import { supabase } from '@/integrations/supabase/client'

interface CampaignPerformanceData {
  leadsContacted: number
  sendVolume: number
  replies: number
  humanReplies: number
  positiveReplies: number
  replyRate: number
  humanReplyRate: number
  positiveRate: number
  meetingsBooked: number
  dealsWon: number
  revenueGenerated: number
  loading?: boolean
  lastUpdated?: string
}

interface SegmentedCampaignPerformanceProps {
  campaigns: Campaign[]
  clients?: Client[]
  performanceData?: Record<string, CampaignPerformanceData> // OPTIMIZED: Accept pre-loaded performance data
  onCampaignClick: (campaign: Campaign, channel?: 'email' | 'linkedin') => void
  onEditDetails: (campaign: Campaign) => void
  onRenameCampaign: (campaign: Campaign) => void
  onCloneCampaign: (campaign: Campaign) => void
  onMoveToArchive: (campaign: Campaign) => void
}

export function SegmentedCampaignPerformance({
  campaigns,
  clients = [],
  performanceData: providedPerformanceData, // OPTIMIZED: Use provided data from parent
  onCampaignClick,
  onEditDetails,
  onRenameCampaign,
  onCloneCampaign,
  onMoveToArchive
}: SegmentedCampaignPerformanceProps) {
  
  // OPTIMIZED: Use provided performance data if available, otherwise fall back to fetching
  // This allows parent component to use optimized materialized view
  const campaignsToLoad = React.useMemo(() => {
    return campaigns.slice(0, 100)
  }, [campaigns])
  
  // FIXED: Only call hook when data is NOT provided to prevent duplicate fetching
  // When providedPerformanceData exists, skip the hook entirely to avoid unnecessary renders and fetches
  const shouldFetch = !providedPerformanceData || Object.keys(providedPerformanceData).length === 0
  const { performanceData: fetchedPerformanceData, loading: isFetching } = useCachedMultipleCampaignsPerformance(
    shouldFetch ? campaignsToLoad : [] // Only fetch if no data provided
  )
  
  // Use provided data if available, otherwise use fetched data
  const campaignPerformance = providedPerformanceData || fetchedPerformanceData
  const isLoading = providedPerformanceData ? false : isFetching
  const campaignOperationalData = useMultipleCampaignsOperationalData(campaigns)
  const [campaignLinkedInStatus, setCampaignLinkedInStatus] = useState<Record<string, boolean>>({})
  const [activeTab, setActiveTab] = useState<'email' | 'linkedin'>('email')
  
  // Filter campaigns based on active tab
  const filteredCampaigns = useMemo(() => {
    if (activeTab === 'linkedin') {
      // Only show campaigns that have LinkedIn variants
      return campaigns.filter(campaign => campaignLinkedInStatus[campaign.id] === true)
    } else {
      // Email tab: show all campaigns (they may or may not have LinkedIn, but we show email stats)
      return campaigns
    }
  }, [campaigns, campaignLinkedInStatus, activeTab])

  // Memoize campaign IDs to prevent unnecessary re-runs
  const campaignIds = useMemo(() => campaigns.map(c => c.id).sort().join(','), [campaigns])
  
  // Direct database query to detect LinkedIn variants (detection only, no performance fetching)
  useEffect(() => {
    let isCancelled = false
    
    const fetchLinkedInStatus = async () => {
      if (!campaigns.length) {
        return
      }
      
      // Prevent multiple simultaneous calls
      if (isCancelled) {
        return
      }
      
      try {
        const campaignIdArray = campaigns.map(c => c.id)

        // Phase M: variants carry campaign_id directly — query them
        // straight from ab_test_variants so 0-segment campaigns
        // (Duel V1) are detected as having LinkedIn when they do.
        const { data: variants, error: variantsError } = await supabase
          .from('ab_test_variants')
          .select('id, campaign_id, channel, name, heyreach_campaign_id')
          .in('campaign_id', campaignIdArray)
          .eq('channel', 'linkedin')
          .not('heyreach_campaign_id', 'is', null)

        if (isCancelled) return

        if (variantsError) {
          console.error('❌ Error fetching LinkedIn variants:', variantsError)
          return
        }

        // campaign_id -> hasLinkedIn
        const linkedInStatus: Record<string, boolean> = {}
        ;(variants || []).forEach(v => {
          if (v.campaign_id) linkedInStatus[v.campaign_id] = true
        })
        if (!isCancelled) {
          setCampaignLinkedInStatus(linkedInStatus)
        }
        
      } catch (error) {
        console.error('❌ Unexpected error fetching LinkedIn status:', error)
        // Don't retry on error to prevent infinite loops
      }
    }
    
    fetchLinkedInStatus()
    
    // Cleanup function to cancel pending requests
    return () => {
      isCancelled = true
    }
  }, [campaignIds]) // Use memoized campaignIds instead of campaigns array
  
  // Removed excessive debug logging to prevent console spam
  // Performance data and LinkedIn status logging disabled for production
  
  const formatNumber = (num: number | undefined) => (num || 0).toLocaleString()
  const formatPercentage = (num: number) => `${num.toFixed(1)}%`

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId)
    return client?.name || 'Unknown Client'
  }

  // Note: findLinkedInCampaignsForCampaign function removed - it was unused and had type errors
  // LinkedIn metrics are now aggregated via aggregateLinkedInMetrics function

  const getEmailStatusColor = (campaignId: string) => {
    const operational = campaignOperationalData[campaignId]
    if (!operational) return 'bg-green-50 text-green-700 border-green-200' // Default green
    
    const totalVariants = operational.totalVariants
    const activeVariants = operational.activeVariants
    
    if (totalVariants === 0) return 'bg-blue-50 text-blue-700 border-blue-200' // Default blue
    
    const activePercentage = (activeVariants / totalVariants) * 100
    
    if (activePercentage > 90) {
      return 'bg-green-50 text-green-700 border-green-200' // Green for >90%
    } else if (activePercentage >= 50) {
      return 'bg-yellow-50 text-yellow-700 border-yellow-200' // Yellow for 50-90%
    } else {
      return 'bg-red-50 text-red-700 border-red-200' // Red for low activity (<50% active)
    }
  }

  const renderChannelStatuses = (campaign: any, channel: 'email' | 'linkedin' = 'email') => {
    const status = getChannelStatus(campaign, channel)
    const isActive = status === 'active'
    
    let channelColor: string
    if (channel === 'linkedin') {
      channelColor = isActive 
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-gray-50 text-gray-400 border-gray-200'
    } else {
      channelColor = isActive 
        ? getEmailStatusColor(campaign.id)
        : 'bg-gray-50 text-gray-400 border-gray-200'
    }
    
    const channelName = channel === 'linkedin' ? 'LinkedIn' : 'Email'
    
    return (
      <div className="flex flex-wrap gap-1">
        <div 
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${channelColor}`}
          title={`${channelName}: ${isActive ? 'Active' : 'Inactive'}`}
        >
          {getChannelIcon(channel)}
          <span>{channelName}</span>
        </div>
      </div>
    )
  }

  // Helper function to format rate with number and percentage (e.g., "14 (8.6%)")
  const formatRateWithPercentage = (count: number, total: number) => {
    if (total === 0) return '0 (0.0%)'
    const percentage = (count / total) * 100
    return `${formatNumber(count)} (${percentage.toFixed(1)}%)`
  }

  // Helper function to render an Email campaign row
  const renderEmailCampaignRow = (campaign: Campaign) => {
    // Get email performance data (should now be EMAIL-ONLY after our fix)
    const rawEmailPerformance = campaignPerformance[campaign.id] || {
      leadsContacted: 0,
      sendVolume: 0,
      replies: 0,
      positiveReplies: 0,
      replyRate: 0,
      positiveRate: 0,
      humanReplies: 0,
      humanReplyRate: 0,
      meetingsBooked: 0,
      dealsWon: 0
    }

    // Email performance — recalculate rates with correct denominators:
    // replyRate = replies / leads contacted (not sends)
    // humanReplyRate = human replies / total replies
    // positiveRate = positive replies / human replies
    const emailPerformance = {
      ...rawEmailPerformance,
      replyRate: rawEmailPerformance.leadsContacted > 0
        ? (rawEmailPerformance.replies / rawEmailPerformance.leadsContacted) * 100 : 0,
      humanReplyRate: rawEmailPerformance.replies > 0
        ? (rawEmailPerformance.humanReplies / rawEmailPerformance.replies) * 100 : 0,
      positiveRate: rawEmailPerformance.humanReplies > 0
        ? (rawEmailPerformance.positiveReplies / rawEmailPerformance.humanReplies) * 100 : 0,
    }

    // Get operational data for this campaign
    const operational = campaignOperationalData[campaign.id] || {
      totalVariants: 0,
      activeVariants: 0,
      pausedVariants: 0,
      completedVariants: 0,
      leadsNotStarted: 0,
      runWay: 0,
      loading: false
    }

    // Use meetingsBooked from emailPerformance (unified with Campaign Detail logic)
    const meetings = emailPerformance.meetingsBooked || 0

    return (
      <TableRow 
        key={campaign.id}
        className="hover:bg-muted/50 cursor-pointer"
        onClick={() => onCampaignClick(campaign)}
      >
        <TableCell className="w-[100px]">
          <div className="font-medium text-foreground text-sm leading-tight break-words">
            {campaign.name}
          </div>
          {campaign.version && (
            <div className="text-xs text-muted-foreground mt-1">
              Version {campaign.version}
            </div>
          )}
        </TableCell>
        <TableCell className="w-[70px]">
          <div className="text-sm font-medium">{getClientName(campaign.client_id)}</div>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium mt-1 inline-block ${
            campaign.campaign_type === 'triggers' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
            campaign.campaign_type === 'bulk' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
            campaign.campaign_type === 'events' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
            'bg-gray-50 text-gray-700 border border-gray-200'
          }`}>
            {campaign.campaign_type?.charAt(0).toUpperCase() + campaign.campaign_type?.slice(1) || 'Unknown'}
          </span>
        </TableCell>
        <TableCell className="w-[70px]">
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded-md text-xs font-medium border ${
              campaign.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
              campaign.status === 'drafted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
              campaign.status === 'ramping' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
              campaign.status === 'paused' ? 'bg-orange-50 text-orange-700 border-orange-200' :
              campaign.status === 'completed' ? 'bg-gray-50 text-gray-700 border-gray-200' :
              'bg-secondary text-secondary-foreground border-border'
            }`}>
              {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1) || 'Unknown'}
            </div>
          </div>
        </TableCell>
        <TableCell className="w-[70px]">
          <span className="text-xs text-muted-foreground">
            {campaign.launched_at ? new Date(campaign.launched_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
          </span>
        </TableCell>
        <TableCell className="w-[70px]">
          <span className="text-xs text-muted-foreground">
            {campaign.completed_at ? new Date(campaign.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
          </span>
        </TableCell>
        <TableCell className="w-[50px]">
          {renderChannelStatuses(campaign, 'email')}
        </TableCell>
        <TableCell className="text-center">
          <span className="font-medium text-sm">{formatNumber(emailPerformance.leadsContacted)}</span>
        </TableCell>
        <TableCell className="text-center">
          <span className="font-medium text-sm">{formatNumber(emailPerformance.sendVolume)}</span>
        </TableCell>
        <TableCell className="text-center">
          <span className="font-medium text-sm">{formatNumber(emailPerformance.replies)}</span>
          <span className="text-xs text-muted-foreground ml-1">{formatPercentage(emailPerformance.replyRate)}</span>
        </TableCell>
        <TableCell className="text-center">
          <span className="font-medium text-blue-600 text-sm">{formatNumber(emailPerformance.humanReplies || 0)}</span>
          <span className="text-xs text-muted-foreground ml-1">{formatPercentage(emailPerformance.humanReplyRate || 0)}</span>
        </TableCell>
        <TableCell className="text-center">
          <span className="font-medium text-green-600 text-sm">{formatNumber(emailPerformance.positiveReplies)}</span>
          <span className="text-xs text-muted-foreground ml-1">{formatPercentage(emailPerformance.positiveRate)}</span>
        </TableCell>
        {/* Meetings Booked and Deals Won cells hidden */}
        <TableCell className="text-center">
          <span className="font-medium text-sm">{formatNumber(operational.leadsNotStarted)}</span>
        </TableCell>
        <TableCell className="text-center">
          <span className="font-medium text-sm">{operational.runWay > 0 ? operational.runWay : 0}</span>
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onCampaignClick(campaign)}>
                <Eye className="h-4 w-4 mr-2" />
                View details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEditDetails(campaign)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRenameCampaign(campaign)}>
                <FileText className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCloneCampaign(campaign)}>
                <Copy className="h-4 w-4 mr-2" />
                Clone campaign
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMoveToArchive(campaign)}>
                <Archive className="h-4 w-4 mr-2" />
                Move to archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    )
  }

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'email':
        return <Mail className="h-3 w-3" />
      case 'linkedin':
        return <Linkedin className="h-3 w-3" />
      case 'cold_call':
      case 'coldcall':
      case 'cold call':
        return <Phone className="h-3 w-3" />
      case 'linkedin_ads':
      case 'linkedin ads':
        return <Megaphone className="h-3 w-3" />
      default:
        return <Mail className="h-3 w-3" />
    }
  }

  const getChannelStatus = (campaign: any, channel: string) => {
    // Special handling for LinkedIn channel - check detection state
    if (channel === 'linkedin') {
      // LinkedIn is active if campaign has LinkedIn variants (use our reliable detection state)
      return campaignLinkedInStatus[campaign.id] ? 'active' : 'inactive'
    }
    
    // Get operational data for this campaign to check if it has active variants
    const operational = campaignOperationalData[campaign.id]
    
    // If no operational data or no active variants, channel is inactive
    if (!operational || operational.activeVariants === 0) {
      return 'inactive'
    }
    
    // For now, if there are active variants, consider all configured channels as potentially active
    // In the future, this could be enhanced to check channel-specific variant status
    if (campaign.channels && campaign.channels.includes(channel)) {
      return 'active'
    }
    return 'inactive'
  }

  // State to store aggregated LinkedIn metrics per campaign
  const [campaignLinkedInMetrics, setCampaignLinkedInMetrics] = useState<Record<string, {
    leadsContacted: number;
    connectionsSent: number;
    connectionsAccepted: number;
    messagesSent: number;
    messageReplies: number;
    inmailsSent: number;
    inmailReplies: number;
    meetingsBooked: number;
  }>>({});

  // Separate LinkedIn aggregator function (different from email aggregation)
  const aggregateLinkedInMetrics = async (campaignIds: string[]) => {
    try {
      // Phase M: variants carry campaign_id directly, so we no longer need
      // the segments table as an intermediate join — this correctly picks
      // up LinkedIn metrics for 0-segment campaigns (e.g. Duel V1).
      const { data: variants } = await supabase
        .from('ab_test_variants')
        .select('id, campaign_id, heyreach_campaign_id, meetings_booked')
        .in('campaign_id', campaignIds)
        .eq('channel', 'linkedin')
        .not('heyreach_campaign_id', 'is', null);

      if (!variants?.length) {
        return {};
      }

      // Group variants by campaign_id
      const variantsByCampaign: Record<string, typeof variants> = {};
      variants.forEach(variant => {
        const campaignId = variant.campaign_id;
        if (!campaignId) return;
        if (!variantsByCampaign[campaignId]) {
          variantsByCampaign[campaignId] = [];
        }
        variantsByCampaign[campaignId].push(variant);
      });

      // REDUCED: Commented out verbose logging
      // console.log(`📊 Grouped variants by campaign:`, Object.keys(variantsByCampaign).map(id => ({
      //   campaignId: id,
      //   variantCount: variantsByCampaign[id].length
      // })));

      // Aggregate metrics for each campaign
      const aggregatedMetrics: Record<string, {
        leadsContacted: number;
        connectionsSent: number;
        connectionsAccepted: number;
        messagesSent: number;
        messageReplies: number;
        inmailsSent: number;
        inmailReplies: number;
        meetingsBooked: number;
      }> = {};

      for (const [campaignId, campaignVariants] of Object.entries(variantsByCampaign)) {
        // REDUCED: Commented out verbose logging
        // console.log(`📊 Aggregating LinkedIn metrics for campaign ${campaignId} (${campaignVariants.length} variants)`);
        
        let totalLeadsContacted = 0;
        let totalConnectionsSent = 0;
        let totalConnectionsAccepted = 0;
        let totalMessagesSent = 0;
        let totalMessageReplies = 0;
        let totalInmailsSent = 0;
        let totalInmailReplies = 0;
        let totalMeetingsBooked = 0;

        // Fetch metrics for all variants in parallel
        const variantPromises = campaignVariants.map(async (variant) => {
          if (!variant.heyreach_campaign_id) return null;

          const campaignIdNum = parseInt(variant.heyreach_campaign_id, 10);
          if (isNaN(campaignIdNum)) {
            console.warn(`⚠️ Invalid heyreach_campaign_id: ${variant.heyreach_campaign_id}`);
            return null;
          }

          try {
            // Fetch all metrics in parallel for this variant
            // Note: Using type assertion because heyreach_linkedin_events is not in generated types
            const [
              connectionsSentResult,
              connectionsAcceptedResult,
              messagesSentResult,
              inmailsSentResult,
              allLeadsResult,
              allRepliesResult
            ] = await Promise.all([
              (supabase.from('heyreach_linkedin_events' as any) as any).select('*', { count: 'exact', head: true }).eq('campaign_id', campaignIdNum).eq('event_type', 'connection_request_sent'),
              (supabase.from('heyreach_linkedin_events' as any) as any).select('*', { count: 'exact', head: true }).eq('campaign_id', campaignIdNum).eq('event_type', 'connection_request_accepted'),
              (supabase.from('heyreach_linkedin_events' as any) as any).select('*', { count: 'exact', head: true }).eq('campaign_id', campaignIdNum).eq('event_type', 'message_sent'),
              (supabase.from('heyreach_linkedin_events' as any) as any).select('*', { count: 'exact', head: true }).eq('campaign_id', campaignIdNum).eq('event_type', 'inmail_sent'),
              (supabase.from('heyreach_linkedin_events' as any) as any).select('lead_id').eq('campaign_id', campaignIdNum).not('lead_id', 'is', null),
              (supabase.from('heyreach_linkedin_events' as any) as any).select('data').eq('campaign_id', campaignIdNum).eq('event_type', 'every_message_reply_received')
            ]);

            const connectionsSent = connectionsSentResult.count || 0;
            const connectionsAccepted = connectionsAcceptedResult.count || 0;
            const messagesSent = messagesSentResult.count || 0;
            const inmailsSent = inmailsSentResult.count || 0;
            const allLeads = (allLeadsResult.data || []) as Array<{ lead_id: string | null }>;
            const allReplies = (allRepliesResult.data || []) as Array<{ data: { is_inmail?: boolean } | null }>;

            const leadsContacted = new Set(allLeads.map(e => e.lead_id).filter(Boolean)).size;
            const messageReplies = allReplies.filter(reply => {
              const isInmail = reply.data?.is_inmail;
              return isInmail === false || isInmail === null || isInmail === undefined;
            }).length || 0;
            const inmailReplies = allReplies.filter(reply => {
              return reply.data?.is_inmail === true;
            }).length || 0;

            return {
              leadsContacted,
              connectionsSent,
              connectionsAccepted,
              messagesSent,
              messageReplies,
              inmailsSent,
              inmailReplies,
              meetingsBooked: variant.meetings_booked || 0
            };
          } catch (variantError) {
            console.error(`❌ Error fetching metrics for variant ${variant.id}:`, variantError);
            return null;
          }
        });

        const variantMetrics = await Promise.all(variantPromises);
        
        // Aggregate all variant metrics
        variantMetrics.forEach(metrics => {
          if (metrics) {
            totalLeadsContacted += metrics.leadsContacted;
            totalConnectionsSent += metrics.connectionsSent;
            totalConnectionsAccepted += metrics.connectionsAccepted;
            totalMessagesSent += metrics.messagesSent;
            totalMessageReplies += metrics.messageReplies;
            totalInmailsSent += metrics.inmailsSent;
            totalInmailReplies += metrics.inmailReplies;
            totalMeetingsBooked += metrics.meetingsBooked;
          }
        });

        aggregatedMetrics[campaignId] = {
          leadsContacted: totalLeadsContacted,
          connectionsSent: totalConnectionsSent,
          connectionsAccepted: totalConnectionsAccepted,
          messagesSent: totalMessagesSent,
          messageReplies: totalMessageReplies,
          inmailsSent: totalInmailsSent,
          inmailReplies: totalInmailReplies,
          meetingsBooked: totalMeetingsBooked,
        };

        // REDUCED: Commented out verbose logging - only log errors
        // console.log(`✅ Aggregated LinkedIn metrics for campaign ${campaignId}:`, aggregatedMetrics[campaignId]);
      }

      // REDUCED: Commented out verbose logging
      // console.log(`✅ Final aggregated metrics for ${Object.keys(aggregatedMetrics).length} campaigns`);
      return aggregatedMetrics;
    } catch (error) {
      console.error('❌ Error aggregating LinkedIn metrics:', error);
      return {};
    }
  };

  // FIXED: Use ref to track previous campaign IDs and prevent duplicate runs
  const prevCampaignIdsRef = React.useRef<string>('');
  
  // Fetch and aggregate LinkedIn metrics for campaigns
  useEffect(() => {
    if (!campaigns.length) return;
    
    // Create stable campaign IDs string
    const campaignIdsString = campaigns.map(c => c.id).sort().join(',');
    
    // Skip if campaign IDs haven't actually changed
    if (campaignIdsString === prevCampaignIdsRef.current) {
      return;
    }
    
    prevCampaignIdsRef.current = campaignIdsString;
    
    const fetchLinkedInMetrics = async () => {
      const campaignIds = campaigns.map(c => c.id);
      const metrics = await aggregateLinkedInMetrics(campaignIds);
      setCampaignLinkedInMetrics(metrics);
    };

    fetchLinkedInMetrics();
  }, [campaigns]); // FIXED: Use campaigns array directly, but check for actual changes with ref

  // Helper function to render a LinkedIn campaign row
  const renderLinkedInCampaignRow = (campaign: Campaign) => {
    // Get operational data for this campaign
    const operational = campaignOperationalData[campaign.id] || {
      totalVariants: 0,
      activeVariants: 0,
      pausedVariants: 0,
      completedVariants: 0,
      leadsNotStarted: 0,
      runWay: 0,
      loading: false
    }

    // Get aggregated LinkedIn metrics for this campaign
    const aggregatedMetrics = campaignLinkedInMetrics[campaign.id] || {
      leadsContacted: 0,
      connectionsSent: 0,
      connectionsAccepted: 0,
      messagesSent: 0,
      messageReplies: 0,
      inmailsSent: 0,
      inmailReplies: 0,
      meetingsBooked: 0
    };

    const linkedInData = {
      leadsContacted: aggregatedMetrics.leadsContacted,
      connectionsSent: aggregatedMetrics.connectionsSent,
      connectionsAccepted: aggregatedMetrics.connectionsAccepted,
      connectionRate: aggregatedMetrics.connectionsSent > 0
        ? (aggregatedMetrics.connectionsAccepted / aggregatedMetrics.connectionsSent) * 100
        : 0,
      messagesSent: aggregatedMetrics.messagesSent,
      messageReplies: aggregatedMetrics.messageReplies,
      messageReplyRate: aggregatedMetrics.messagesSent > 0
        ? (aggregatedMetrics.messageReplies / aggregatedMetrics.messagesSent) * 100
        : 0,
      inmailsSent: aggregatedMetrics.inmailsSent,
      inmailReplies: aggregatedMetrics.inmailReplies,
      inmailReplyRate: aggregatedMetrics.inmailsSent > 0
        ? (aggregatedMetrics.inmailReplies / aggregatedMetrics.inmailsSent) * 100
        : 0,
      meetingsBooked: aggregatedMetrics.meetingsBooked
    }

    return (
      <TableRow 
        key={campaign.id}
        className="hover:bg-muted/50 cursor-pointer"
        onClick={() => onCampaignClick(campaign, 'linkedin')}
      >
        <TableCell className="w-[100px]">
          <div className="font-medium text-foreground text-sm leading-tight break-words">
            {campaign.name}
          </div>
          {campaign.version && (
            <div className="text-xs text-muted-foreground mt-1">
              Version {campaign.version}
            </div>
          )}
        </TableCell>
        <TableCell className="w-[70px]">
          <div className="text-sm font-medium">{getClientName(campaign.client_id)}</div>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium mt-1 inline-block ${
            campaign.campaign_type === 'triggers' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
            campaign.campaign_type === 'bulk' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
            campaign.campaign_type === 'events' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
            'bg-gray-50 text-gray-700 border border-gray-200'
          }`}>
            {campaign.campaign_type?.charAt(0).toUpperCase() + campaign.campaign_type?.slice(1) || 'Unknown'}
          </span>
        </TableCell>
        <TableCell className="w-[70px]">
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded-md text-xs font-medium border ${
              campaign.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
              campaign.status === 'drafted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
              campaign.status === 'ramping' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
              campaign.status === 'paused' ? 'bg-orange-50 text-orange-700 border-orange-200' :
              campaign.status === 'completed' ? 'bg-gray-50 text-gray-700 border-gray-200' :
              'bg-secondary text-secondary-foreground border-border'
            }`}>
              {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1) || 'Unknown'}
            </div>
          </div>
        </TableCell>
        <TableCell className="w-[50px]">
          {renderChannelStatuses(campaign, 'linkedin')}
        </TableCell>
        <TableCell className="text-right">
          <span className="font-medium text-sm">{formatNumber(linkedInData.leadsContacted)}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-medium text-sm">{formatNumber(linkedInData.connectionsSent)}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-medium text-sm">{formatRateWithPercentage(linkedInData.connectionsAccepted, linkedInData.connectionsSent)}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-medium text-sm">{formatNumber(linkedInData.messagesSent)}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-medium text-sm">{formatRateWithPercentage(linkedInData.messageReplies, linkedInData.messagesSent)}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-medium text-sm">{formatNumber(linkedInData.inmailsSent)}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-medium text-sm">{formatRateWithPercentage(linkedInData.inmailReplies, linkedInData.inmailsSent)}</span>
        </TableCell>
        {/* Meetings Booked cell hidden */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onCampaignClick(campaign, 'linkedin')}>
                <Eye className="h-4 w-4 mr-2" />
                View details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEditDetails(campaign)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRenameCampaign(campaign)}>
                <FileText className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCloneCampaign(campaign)}>
                <Copy className="h-4 w-4 mr-2" />
                Clone campaign
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMoveToArchive(campaign)}>
                <Archive className="h-4 w-4 mr-2" />
                Move to archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    )
  }

  // Performance data is now handled by the cached hook

  return (
    <div className="space-y-6">
      {/* Campaign Performance Table with Tabs */}
      <div className="border border-border rounded-lg overflow-hidden mx-4">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'email' | 'linkedin')} className="w-full">
          <div className="px-4 pt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Outreach
              </TabsTrigger>
              <TabsTrigger value="linkedin" className="flex items-center gap-2">
                <Linkedin className="h-4 w-4" />
                LinkedIn Outreach
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Email Tab */}
          <TabsContent value="email" className="mt-0">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-medium">Campaign</TableHead>
                  <TableHead className="font-medium">Client</TableHead>
                  <TableHead className="font-medium">Campaign Status</TableHead>
                  <TableHead className="font-medium">Launched Date</TableHead>
                  <TableHead className="font-medium">Completed Date</TableHead>
                  <TableHead className="font-medium">Channel Status</TableHead>
                  <TableHead className="font-medium text-center">Leads Contacted</TableHead>
                  <TableHead className="font-medium text-center">Send Volume</TableHead>
                  <TableHead className="font-medium text-center">Replies</TableHead>
                  <TableHead className="font-medium text-center">Human Replies</TableHead>
                  <TableHead className="font-medium text-center">Positive Replies</TableHead>
                  {/* Meetings Booked and Deals Won hidden until data pipeline is ready */}
                  <TableHead className="font-medium text-center">Leads Not Started</TableHead>
                  <TableHead className="font-medium text-center">Run-Way (Days)</TableHead>
                  <TableHead className="font-medium text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={17} className="text-center py-8 text-muted-foreground">
                      No campaigns found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCampaigns.map((campaign) => renderEmailCampaignRow(campaign))
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* LinkedIn Tab */}
          <TabsContent value="linkedin" className="mt-0">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-medium">Campaign</TableHead>
                  <TableHead className="font-medium">Client</TableHead>
                  <TableHead className="font-medium">Campaign Status</TableHead>
                  <TableHead className="font-medium">Channel Status</TableHead>
                  <TableHead className="font-medium text-right">Leads Contacted</TableHead>
                  <TableHead className="font-medium text-right">Connections Sent</TableHead>
                  <TableHead className="font-medium text-right">Connection Rate</TableHead>
                  <TableHead className="font-medium text-right">Messages Sent</TableHead>
                  <TableHead className="font-medium text-right">Messages Reply Rate</TableHead>
                  <TableHead className="font-medium text-right">InMails Sent</TableHead>
                  <TableHead className="font-medium text-right">InMail Reply Rate</TableHead>
                  {/* Meetings Booked hidden until data pipeline is ready */}
                  <TableHead className="w-[60px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-8 text-muted-foreground">
                      No LinkedIn campaigns found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCampaigns.map((campaign) => renderLinkedInCampaignRow(campaign))
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}